import {
    RecoverySession,
    RecoveryMethod,
} from "./types";


// Mock Data
const MOCK_USERS = [
    {
        id: "user-1",
        email: "user@example.com",
        username: "user123",
        phone: "+15550001234",
        password: "Password123!",
    },
    {
        id: "user-2",
        email: "admin@example.com",
        username: "adminUser",
        phone: "+15550005678",
        password: "AdminPassword1!",
    },
    {
        id: "user-3",
        email: "employee@example.com",
        username: "employeeUser",
        phone: "", // No phone for employee in this scenario
        password: "EmployeePassword1!",
    },
    {
        id: "user-4",
        email: "employee-phone@example.com",
        username: "employeePhone",
        phone: "+15550009999",
        password: "EmployeePassword2!",
    },
];

// In-memory store
class RecoveryStore {
    private sessions: Map<string, RecoverySession> = new Map();
    private methods: Map<string, RecoveryMethod[]> = new Map();

    constructor() {
        // Seed methods for mock users
        this.methods.set("user-1", [
            {
                id: "meth_email_1",
                type: "email_link",
                value: "u***@example.com",
                is_primary: true,
            },
            {
                id: "meth_phone_1",
                type: "sms_otp", // Flow B Identity
                value: "+1 *** *** 1234",
                is_primary: false,
            },
            {
                id: "meth_backup_1",
                type: "backup_code", // Authority Only
                value: "xxxx-xxxx-xxxx",
                is_primary: false,
            },
        ]);

        // Admin User (User 2) - Email + SMS (Multi-flow)
        this.methods.set("user-2", [
            {
                id: "meth_email_2",
                type: "email_link",
                value: "a***@example.com",
                is_primary: true,
            },
            {
                id: "meth_phone_2",
                type: "sms_otp",
                value: "+1 *** *** 5678",
                is_primary: false,
            },
            {
                id: "meth_backup_2",
                type: "backup_code",
                value: "xxxx-xxxx-xxxx",
                is_primary: false,
            },
        ]);

        // Employee User (User 3) - Email Only (One-step to reset)
        this.methods.set("user-3", [
            {
                id: "meth_email_3",
                type: "email_link",
                value: "e***@example.com",
                is_primary: true,
            },
        ]);

        // Employee User Check (User 4) - Phone Only (One-step to reset)
        this.methods.set("user-4", [
            {
                id: "meth_phone_4",
                type: "sms_otp",
                value: "+1 *** *** 9999",
                is_primary: true,
            },
        ]);
    }

    createSession(emailOrUsername: string, ip: string, ua: string): RecoverySession {
        const normalizedInput = emailOrUsername.trim().toLowerCase();
        let user = MOCK_USERS.find(
            (u) => u.email.toLowerCase() === normalizedInput || u.username.toLowerCase() === normalizedInput
        );

        if (!user) {
            console.warn(`[Mock Store] Email "${emailOrUsername}" not found. Falling back to admin@example.com (user-2) for testing.`);
            user = MOCK_USERS.find(u => u.email === "admin@example.com") || MOCK_USERS[1];
        }

        const session: RecoverySession = {
            recovery_session_id: `sess-${Date.now()}`,
            user_id: user.id,
            tenant_id: "tenant-1",
            status: "identified",
            current_step: "initiate",
            attempt_count: 0,
            expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
            risk_snapshot: {
                ip_address: ip,
                user_agent: ua,
                device_fingerprint: "mock-fingerprint",
            },
            next_allowed_methods: ["email_link", "sms_otp"],
            history: [
                {
                    stage: "identified",
                    status: "success",
                    timestamp: new Date().toISOString(),
                    metadata: { method: "lookup" },
                },
            ],
        };

        this.sessions.set(session.recovery_session_id, session);
        return session;
    }

    getSession(sessionId: string): RecoverySession | undefined {
        // Check expiry
        const session = this.sessions.get(sessionId);
        if (!session) return undefined;

        if (new Date(session.expires_at) < new Date()) {
            session.status = "expired";
            this.sessions.set(sessionId, session); // update status
        }
        return session;
    }

    getMethods(userId: string): RecoveryMethod[] {
        return this.methods.get(userId) || [];
    }

    updateSession(session: RecoverySession): void {
        this.sessions.set(session.recovery_session_id, session);
    }

    // --- Token Helpers ---
    // In a real app backend these would be signed JWTs.
    // For mocks, we'll use simple format: "type_sessionId_timestamp"

    generatePreAuthToken(sessionId: string): string {
        return `pre_${sessionId}_${Date.now()}`;
    }

    generateSessionToken(sessionId: string): string {
        return `sess_${sessionId}_${Date.now()}`;
    }

    generateResetToken(sessionId: string): string {
        return `reset_${sessionId}_${Date.now()}`;
    }

    generateVerificationToken(sessionId: string): string {
        return `verif_${sessionId}_${Date.now()}`;
    }

    parseToken(token: string): { sessionId: string; type: string } | null {
        const parts = token.split("_");
        if (parts.length < 3) return null;
        return { type: parts[0], sessionId: parts[1] }; // Assuming simple structure
    }
}

export const mockRecoveryStore = new RecoveryStore();
